import { useQuery } from '@apollo/client';
import type React from 'react';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { useAppState } from 'state';
import styled, { useTheme } from 'styled-components';

import Typography from 'app/ui/components/atoms/typography';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import VideoPlayer from 'app/ui/components/molecules/video-component';
import ResourceListGrid from 'src/app/ui/components/molecules/resource-list-card/grid';

import { Footer, HtmlTitle, Navbar, PageContainer } from 'app/ui/components/layouts/generic-page';

import { getEquipmentIcon } from 'app/ui/components/atoms/icons/equipment/util';
import { lengthFromDurationSeconds } from 'app/utils/duration-handler';

import { Challenge as CHALLENGE } from 'app/pages/challenge/query.gql';
import { ChallengeUserResources as USER_RESOURCES } from 'app/pages/challenge/user-resources.gql';
import type {
  ChallengeQuery,
  ChallengeQueryVariables,
  ChallengeType,
  ChallengeUserResourcesQuery,
  ChallengeUserResourcesQueryVariables,
  Video,
} from 'app/types/graphql';

import type { LessonSection, LessonSection as LessonSectionType } from 'app/types/graphql';
import type { ContentData, GroupContentItemData } from 'app/ui/components/molecules/content-item-areas-wrapper';

import { type VideoData, type WorkoutType, flattenSectionsToExercises, getVideoData } from 'app/pages/workout/util';

import { sectionToData } from 'app/pages/workout/util/data';
import ExerciseItem from 'app/ui/components/molecules/exercise-item';
import { ExercisePickerProvider } from 'app/ui/components/molecules/picker/exercise/util';

import useConfig from 'app/config';
import { useAlternativeSelections } from 'app/hooks/use-alternative-exercises';
import { useChallengeTypeIcon } from 'app/hooks/use-challenge-type-icon';
import Slideshow from 'app/ui/components/molecules/slideshow';
import { mapLessonToSlides } from 'app/ui/components/molecules/whiteboard-slide/util';
import { useCommunicationService } from 'src/app/services/communication';
import WhiteboardButton from 'src/app/ui/components/molecules/whiteboard-button';
import { trackEvent } from 'src/app/utils/analytics/amplitude/tracker';
import { MessageType } from 'src/app/utils/in-app-comunication/types';
import { getPathFromUrl, isInIframe } from 'src/app/utils/in-app-comunication/utils';
import CustomTooltip from 'src/app/ui/components/molecules/tooltip';
import QuestionMarkCircle from 'src/app/ui/components/atoms/icons/question-mark-circle';
import type { Lesson } from 'src/types/graphql';

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;

  ${({ theme: { spacing } }) => `
    padding: ${spacing(8)};
  `}

  ${({ theme: { media, spacing } }) => `${media.medium(
    () => `
      display: flex;
      padding: ${spacing(8)};
    `,
  )}
  `};

  ${({ theme: { media, spacing } }) => `${media.large(
    () => `
      padding: 0  ${spacing(10)};
      max-width: 67.5rem;
      padding: 0 2.1875rem;
    `,
  )}
  `};
`;

const ChallengeTopLayer = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme: { media } }) => `${media.large(
    () => `
      max-width: 60%;
      flex: 3;
    `,
  )}
  `};
`;

const Breakdown = styled.div`
  word-break: break-word;
`;

const BreakdownWrapper = styled(Breakdown)`
  display: flex;
  flex-direction: column;
`;

const Video = styled.div`
  width: 80%;

  ${({ theme: { spacing } }) => `
    margin-bottom: ${spacing(8)};
  `}

  ${({ theme: { media } }) => `${media.medium(
    () => `
      margin: 0;
      width: 90%;
    `,
  )}
  `};
`;

const Title = styled(Typography)`
  text-transform: uppercase;
`;

const Description = styled(Typography)`
  margin-bottom: 1rem;

  ${({ theme: { media, spacing } }) => `${media.medium(
    () => `
      padding-right: ${spacing(4)};
    `,
  )}
  `};
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;

  ${({ theme: { spacing } }) => `
    gap: ${spacing(4)};
    margin: ${spacing(6)} 0};
  `}

  label {
    text-transform: uppercase;
  }
`;

const EquipmentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${({ theme: { spacing } }) => `
    gap: ${spacing(2)};
  `}

  label {
    text-transform: uppercase;
  }
`;

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${({ theme: { spacing } }) => `
    gap: ${spacing(2)};
  `}

  label {
    text-transform: uppercase;
  }
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${({ theme: { spacing } }) => `
    width: ${spacing(4)};
    height: ${spacing(4)};
  `}

  img {
    width: 100%;
    height: 100%;
  }
`;

const StyledVideoPlayer = styled(VideoPlayer)`
  aspect-ratio: 1;
  ${({ theme: { media } }) => `${media.medium(
    () => `
    aspect-ratio: 0.75;
    `,
  )}
  `};
`;

const StyledImage = styled.img`
  width: 100%;
  object-fit: cover;
  aspect-ratio: 1;
  ${({ theme: { spacing } }) => `border-radius: ${spacing(2)};`}
  ${({ theme: { media } }) => `${media.medium(
    () => `
      aspect-ratio: 0.75;
    `,
  )}
  `};
`;

const ChallengeMediaWrapper = styled.div<{ $isSticky: boolean }>`
  width: 100%;
  ${({ $isSticky }) => $isSticky && 'position: sticky;'}
  top: 4.75em;
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: fit-content;
  z-index: 2;

  ${({ theme: { colors } }) => `background: ${colors.black};`}

  ${({ theme: { media }, $isSticky }) => `${media.medium(
    () => `
      flex: 2;
      justify-content: flex-end;
      background: transparent;
      ${$isSticky ? 'position: sticky;' : ''}
    `,
  )}
  `};
`;

const DesktopStructure = styled.div`
  display: none;
  ${({ theme: { media } }) => `${media.large(
    () => `
      display: flex;
      flex-direction: row;
      width: 67.5rem;
      align-items: flex-start;
    `,
  )}
  `};
`;

const StickyContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  
  ${({ theme: { media, spacing } }) => `${media.medium(
    () => `
      min-height: 0;
      margin-bottom: ${spacing(2)};
    `,
  )}
  `};
`;

const MobileStructure = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme: { media } }) => `${media.large(
    () => `
      display: none;
    `,
  )}
  `};
`;

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionItem = styled.div`
  margin: 0.5rem 0;
  ${({ theme: { colors } }) => `
    background: ${colors.black};
    color: ${colors.white};
  `}
`;

const SectionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border-top: 1px solid ${({ theme: { colors } }) => colors.focoltone};
  border-bottom: 1px solid ${({ theme: { colors } }) => colors.focoltone};
  padding: ${({ theme: { spacing } }) => `${spacing(1.5)} ${spacing(3)}`};

  h6 {
    color: ${({ theme: { colors } }) => colors.white};
  }
`;

const SectionContent = styled.div`
  display: block;
  padding: ${({ theme: { spacing } }) => `${spacing(4)} 0`};
`;

const SectionDescription = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => `${spacing(4)}`};
  display: flex;
  flex-direction: column;
`;

const TitleWithContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme: { spacing } }) => `${spacing(4)}`};
  flex: 1;
`;

const AdditionalInfoSection = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1rem;
  flex-shrink: 0;
  padding-left: 0.5rem;

  ${({ theme: { media } }) => `${media.medium(
    () => `
        display: flex;
    `,
  )}
  `};
`;

const ExpandableTextWrapper = styled.div`
  margin: ${({ theme }) => `${theme.spacing(4)} 0`};
`;

const ExpandedContentItemArea = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const ExpandedContentItem = styled.div`
  width: 100%;
`;

const ExpandedGroupTitle = styled(Typography)`
  margin: ${({ theme: { spacing } }) => `${spacing(4)} 0 ${spacing(2)} 0`};
  font-weight: ${({ theme }) => theme.typography.bold};
`;

const ExpandedGroupDescription = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => `${spacing(2)}`};
`;

const ResourceSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme: { colors, spacing } }) => `
    border-top: 2px solid ${colors.devilsGray};
    margin-top: ${spacing(10)};
    padding: ${spacing(12)} ${spacing(8)} 0;
  `}

  ${({ theme: { media } }) => `${media.medium(
    () => `
      width: 67.5rem;
    `,
  )}
  `};
`;

const ResourceTitle = styled(Typography)`
  margin-bottom: ${({ theme: { spacing } }) => `${spacing(4)}`};
`;

const ResourceDescription = styled(Typography)`
  margin-bottom: ${({ theme: { spacing } }) => `${spacing(8)}`};
`;

const ResourceSectionsContainer = styled.div`
  width: 100%;

  ${({ theme: { media, spacing } }) => `${media.medium(
    () => `
      display: flex;
      flex-direction: column;
      gap: ${spacing(8)};
    `,
  )}
  `};
`;

const ResourceSectionItem = styled.div<{ $isEven: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => `${spacing(8)}`};
  margin-bottom: ${({ theme: { spacing } }) => `${spacing(8)}`};

  ${({ theme: { media, spacing }, $isEven }) => `${media.medium(
    () => `
      flex-direction: row;
      gap: ${spacing(8)};
      align-items: start;
      ${$isEven ? 'flex-direction: row-reverse;' : ''}
    `,
  )}
  `};
`;

const ResourceSectionText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => `${spacing(4)}`};
`;

const ResourceSectionContent = styled.div`
  width: 100%;
  ${({ theme: { media } }) => `${media.medium(
    () => `
     width: 86%;
     margin-left: 17px;
    `,
  )}
  `};
`;

const ResourceSectionImage = styled.img`
  width: 100%;
  ${({ theme: { spacing } }) => `border-radius: ${spacing(2)};`}
`;

const ResourceSectionMediaWrapper = styled.div`
  width: 100%;
  flex: 1;

  ${({ theme: { media } }) => `${media.medium(
    () => `
      width: 50%;
      min-width: 400px;
    `,
  )}
  `};
`;

const ImageTracker = ({ resource_id, resource_title }: { resource_id: string; resource_title: string }) => {
  useEffect(() => {
    trackEvent('resource_viewed', {
      resource_id,
      resource_title,
      resource_type: 'image',
    });
  }, [resource_id, resource_title]);

  return null;
};

const PlaybookTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const MoreContentWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(20)};
`;

type ChallengeBreakdownProps = {
  challenge: {
    id: string;
    title: string;
    longDescription?: string | null;
    shortDescription?: string | null;
    notes?: string | null;
    challengeType?: ChallengeType | null;
  };
  lesson: Omit<Lesson, 'sections' | 'trainers' | 'collectionOnly'>;
  workout: WorkoutType;
};

type ChallengeMediaProps = {
  videoData: VideoData | null;
  challengeImageUrl: string;
  isSticky: boolean;
  autoPlay?: boolean;
};

type ExpandedSectionProps = {
  section: ContentData;
};

const ChallengeBreakdown: FC<ChallengeBreakdownProps> = ({ challenge, lesson, workout }) => {
  const theme = useTheme();
  const location = useLocation();
  const comms = useCommunicationService();
  const intl = useIntl();

  const { config } = useConfig();
  const isWhiteboardEnabled = config.REACT_APP_WHITEBOARD_ENABLED;

  const selectedAlternatives = useAlternativeSelections();
  const challengeTypeIcon = useChallengeTypeIcon(challenge.challengeType);

  const queryParams = new URLSearchParams(location.search);
  const isPresenterView = queryParams.get('isPresenterView') !== 'false';
  const isSlideshowOn = queryParams.get('isSlideshowOn') === 'true';

  const [slides, setSlides] = useState<React.JSX.Element[]>(
    workout ? mapLessonToSlides({ workout, isPresenterView, selectedAlternatives }) : [],
  );

  const [showWhiteboard, setShowWhiteboard] = useState(isSlideshowOn);

  const description = challenge.longDescription || challenge.shortDescription || lesson?.description || '';

  const getTooltipText = (key: string | undefined): string | null => {
    const messageId = `challenges.tooltip.${key}`;

    // If the key does not exist in the messages, return null
    if (!key || !intl.messages[messageId]) {
      return null;
    }

    return intl.formatMessage({ id: messageId });
  };

  const handlePresentationButtonClick = () => {
    if (workout) {
      setSlides(mapLessonToSlides({ workout, isPresenterView, selectedAlternatives }));
      setShowWhiteboard(true);
      if (isInIframe()) {
        comms.sendMessage({
          type: MessageType.WHITEBOARD_OPEN,
          url: getPathFromUrl(window.location.href),
          workoutName: workout.name,
        });
      }
      trackEvent('whiteboard_opened', {
        workout_id: challenge.id,
        workout_name: challenge.title,
        is_challenge: true,
      });
    }
  };

  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.ATHLETE_VIEW_OPEN) {
        if (message.url) {
          window.location.href = message.url;
        }
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [comms]);

  return (
    <Breakdown>
      <Title variant="h2">{challenge.title}</Title>
      {lesson && (
        <DetailsWrapper>
          {challengeTypeIcon && (
            <DetailWrapper>
              <Icon>{challengeTypeIcon.icon}</Icon>
              <Typography variant="label">{challengeTypeIcon.text}</Typography>
              <CustomTooltip
                tooltipText={getTooltipText(challenge.challengeType?.toLowerCase())}
                maxWidth="350px"
                placement="right"
              >
                <QuestionMarkCircle color={theme.colors.paragraphGray} />
              </CustomTooltip>
            </DetailWrapper>
          )}
          {lesson.equipment &&
            lesson.equipment.length > 0 &&
            lesson.equipment
              .map((eq) => {
                const equipmentIcon = getEquipmentIcon({
                  name: eq.name,
                  color: theme.colors.white,
                });
                return equipmentIcon ? { icon: equipmentIcon, name: eq.name } : null;
              })
              .filter((item): item is { icon: React.JSX.Element; name: string } => item !== null)
              .map((item) => (
                <EquipmentWrapper key={item.name}>
                  <Icon>{item.icon}</Icon>
                  <Typography variant="label">{item.name}</Typography>
                </EquipmentWrapper>
              ))}
        </DetailsWrapper>
      )}
      {description && (
        <Description variant="body2" color={theme.colors.paragraphGray}>
          {description}
        </Description>
      )}
      {challenge.notes && (
        <ExpandableTextWrapper>
          <Typography variant="caption" color={theme.colors.paragraphGray} htmlContent={challenge.notes} />
        </ExpandableTextWrapper>
      )}
      {isWhiteboardEnabled && <WhiteboardButton onOpenWhiteboard={handlePresentationButtonClick} workout={workout} />}

      {isWhiteboardEnabled && showWhiteboard && (
        <Slideshow
          isPresenterView={isPresenterView}
          slides={slides}
          onEndWhiteboard={() => setShowWhiteboard(false)}
          workout={workout}
          isChallenge={true}
        />
      )}
    </Breakdown>
  );
};

const ChallengeMedia: FC<ChallengeMediaProps> = ({
  videoData,
  challengeImageUrl,
  isSticky,
  autoPlay = true,
}: ChallengeMediaProps) => (
  <ChallengeMediaWrapper $isSticky={isSticky}>
    <Video>
      {videoData ? (
        <StyledVideoPlayer
          duration={videoData.duration}
          src={videoData.src}
          title={videoData.name}
          thumbnail={videoData.thumbnail}
          key={videoData.key}
          loop={true}
          className="challengeVideoPlayer"
          autoPlay={autoPlay}
        />
      ) : (
        <StyledImage src={challengeImageUrl} />
      )}
    </Video>
  </ChallengeMediaWrapper>
);

const ExpandedGroupRenderer: FC<{
  group: GroupContentItemData;
  groupId: string;
}> = ({ group, groupId }) => {
  const theme = useTheme();
  const { id, title, content, showTitle, sectionExercises, nestedGroups } = group;

  return (
    <ExpandedContentItemArea key={`group-${id}`}>
      <ExpandedContentItem>
        {showTitle && (
          <>
            <ExpandedGroupTitle variant="body2" color={theme.colors.paragraphGray}>
              {title}
            </ExpandedGroupTitle>
            {content && typeof content === 'string' && content.trim().length > 0 && (
              <ExpandedGroupDescription>
                <Typography variant="caption" color={theme.colors.paragraphGray}>
                  {content}
                </Typography>
              </ExpandedGroupDescription>
            )}
          </>
        )}
        {sectionExercises?.length > 0 && (
          <div>
            {sectionExercises.map((sectionExercise, index) => (
              <ExerciseItem
                key={sectionExercise.id}
                isOpen={true}
                groupId={groupId}
                initialExercise={sectionExercise}
                onClick={() => undefined}
                index={index}
                hideChevrons={true}
                hideContent={true}
              />
            ))}
          </div>
        )}
        {nestedGroups?.map((nestedGroup) => (
          <ExpandedGroupRenderer key={`nested-${nestedGroup.id}`} group={nestedGroup} groupId={groupId} />
        ))}
      </ExpandedContentItem>
    </ExpandedContentItemArea>
  );
};

const ExpandedContentItemsWrapper: FC<{
  items: GroupContentItemData[];
}> = ({ items }) => {
  if (!items.length) {
    return <></>;
  }

  return (
    <ExercisePickerProvider>
      {items.map((item) => (
        <ExpandedGroupRenderer key={`group-${item.id}`} group={item} groupId={item.id} />
      ))}
    </ExercisePickerProvider>
  );
};

const ExpandedSection: FC<ExpandedSectionProps> = ({ section }) => {
  const theme = useTheme();

  return (
    <SectionItem id={section.id} data-testid="challenge-section-item">
      <SectionTitle>
        <TitleWithContent>
          <Typography variant="h6">{section.title}</Typography>
          {section.titleExtraContent}
        </TitleWithContent>
        <AdditionalInfoSection>
          <Typography variant="h6" color={theme.colors.lightGray}>
            {section.additionalInfo}
          </Typography>
        </AdditionalInfoSection>
      </SectionTitle>
      <SectionContent>
        {section.content && (
          <SectionDescription>
            <Typography variant="body2" color={theme.colors.paragraphGray}>
              {section.content}
            </Typography>
          </SectionDescription>
        )}
        {section.groups && <ExpandedContentItemsWrapper items={section.groups} />}
        {section.notes && (
          <ExpandableTextWrapper>
            <Typography variant="caption" color={theme.colors.paragraphGray} htmlContent={section.notes} />
          </ExpandableTextWrapper>
        )}
      </SectionContent>
    </SectionItem>
  );
};

const ChallengeSections: FC<{
  sections: ContentData[];
}> = ({ sections }) => {
  return (
    <SectionsWrapper>
      {sections.map((section) => (
        <ExpandedSection key={section.id} section={section} />
      ))}
    </SectionsWrapper>
  );
};

type ResourceSectionData = {
  id: string;
  title: string;
  content?: string | null;
  images?: Array<{ signedUrl?: string | null } | null> | null;
  videos?: Array<{
    id: number;
    signedUrl?: string | null;
    thumbnail?: { signedUrl?: string | null } | null;
    subtitles?: { signedUrl?: string | null } | null;
  } | null> | null;
};

type ResourcePageData = {
  title: string;
  content?: string | null;
  sections?: Array<ResourceSectionData | null> | null;
};

const ResourceSection: FC<{
  section: ResourceSectionData;
  index: number;
}> = ({ section, index }) => {
  const theme = useTheme();
  const imageUrl = section.images?.find((img) => img?.signedUrl)?.signedUrl || '';
  const video = section.videos?.find((v) => v?.signedUrl) || null;
  const isEven = index % 2 === 1;

  return (
    <ResourceSectionItem $isEven={isEven}>
      <ResourceSectionText>
        <Typography variant="h3" color={theme.colors.white}>
          {section.title}
        </Typography>
        <ResourceSectionContent>
          {section.content && (
            <Typography variant="body3" color={theme.colors.paragraphGray} htmlContent={section.content} />
          )}
        </ResourceSectionContent>
      </ResourceSectionText>
      {(video?.signedUrl || imageUrl) && (
        <ResourceSectionMediaWrapper>
          {video?.signedUrl ? (
            <VideoPlayer
              src={video.signedUrl}
              title=""
              duration=""
              showRewindAndForward={true}
              trackEventOnClickPlay={() =>
                trackEvent('resource_viewed', {
                  resource_id: section.id,
                  resource_title: section.title,
                  resource_type: 'video',
                  from_challenge: true,
                })
              }
              thumbnail={video.thumbnail?.signedUrl || undefined}
              subtitle={null}
              isCentered={true}
            />
          ) : (
            <>
              <ImageTracker resource_id={section.id} resource_title={section.title} />
              <ResourceSectionImage src={imageUrl} alt={section.title} />
            </>
          )}
        </ResourceSectionMediaWrapper>
      )}
    </ResourceSectionItem>
  );
};

const ResourcePageSection: FC<{
  resourcePage: ResourcePageData | null | undefined;
}> = ({ resourcePage }) => {
  const theme = useTheme();

  if (!resourcePage || !resourcePage.sections || resourcePage.sections.length === 0) {
    return null;
  }

  return (
    <ResourceSectionWrapper>
      <ResourceTitle variant="h2" color={theme.colors.white}>
        {resourcePage.title}
      </ResourceTitle>
      {resourcePage.content && (
        <ResourceDescription variant="body2" color={theme.colors.paragraphGray} htmlContent={resourcePage.content} />
      )}
      <ResourceSectionsContainer>
        {resourcePage.sections.map((section, index) => {
          if (!section) {
            return null;
          }
          return <ResourceSection key={section.id} section={section} index={index} />;
        })}
      </ResourceSectionsContainer>
    </ResourceSectionWrapper>
  );
};

const Challenge: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const intl = useIntl();
  const { config } = useConfig();
  const isMobile = useMemo(() => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent), []);

  const userId = useAppState(({ auth }) => auth.userId) as number | null;

  const [contentItemFocused, setContentItemFocused] = useState<string | null>(null);
  const [exercisePicked, setExercisePicked] = useState<string | null>(null);
  const [videoData, setVideoData] = useState<VideoData | null>(null);

  const { data, loading, error } = useQuery<ChallengeQuery, ChallengeQueryVariables>(CHALLENGE, {
    variables: { id: id || '' },
    skip: !id,
  });

  const { data: userResourcesData } = useQuery<ChallengeUserResourcesQuery, ChallengeUserResourcesQueryVariables>(
    USER_RESOURCES,
    {
      variables: {
        userId: userId || 0,
        resourceListId: config.REACT_APP_ROOT_RESOURCE_LIST_ID,
      },
      skip: !userId || !config.REACT_APP_ROOT_RESOURCE_LIST_ID,
    },
  );

  const handleSectionFocusChange = useCallback(
    ({
      sectionExerciseKey,
      exerciseKey,
    }: { sectionKey?: string | null; sectionExerciseKey?: string | null; exerciseKey?: string | null }) => {
      if (typeof sectionExerciseKey !== 'undefined') {
        setContentItemFocused(sectionExerciseKey);
      }

      if (typeof exerciseKey !== 'undefined') {
        setExercisePicked(exerciseKey);
      }
    },
    [],
  );

  // Calculate video data using useMemo to ensure it's always calculated before early returns
  const allExercises = useMemo(() => {
    if (!data?.challenge?.lesson?.sections) {
      return [];
    }
    return flattenSectionsToExercises(data.challenge.lesson.sections as LessonSectionType[]);
  }, [data?.challenge?.lesson?.sections]);

  const videoDataFromAccordion = useMemo(() => {
    return getVideoData({
      focusedContentItem: contentItemFocused,
      exercises: allExercises,
      exercisePicked,
    });
  }, [contentItemFocused, allExercises, exercisePicked]);

  useEffect(() => {
    setVideoData(videoDataFromAccordion);
  }, [videoDataFromAccordion]);

  // Track challenge opened event when challenge data is loaded
  useEffect(() => {
    if (data?.challenge && !loading) {
      trackEvent('challenge_opened', {
        challenge_id: data.challenge.id,
        challenge_title: data.challenge.title,
      });
    }
  }, [data?.challenge, loading]);

  const workout: WorkoutType | null = useMemo(() => {
    const challenge = data?.challenge;
    const lesson = challenge?.lesson;
    if (!lesson || !challenge) {
      return null;
    }
    return {
      id: lesson.id,
      migratedAt: null,
      name: challenge.title,
      trainers: '',
      duration: lengthFromDurationSeconds(lesson.duration),
      concept: lesson.concept?.name || '',
      intensity: `RPE ${lesson.intensity}`,
      sections: (lesson.sections || []) as LessonSection[],
      imageUrl: challenge.challengeImage?.signedUrl || '',
      description: challenge.longDescription || challenge.shortDescription || lesson.description || '',
      equipment: lesson.equipment || [],
      isCompleted: false,
      isSaved: false,
    };
  }, [data?.challenge, data?.challenge?.lesson]);

  if (!id) {
    return <ErrorComponent />;
  }

  if (loading) {
    return <Loading />;
  }

  if (error || !data || !data.challenge || !data.challenge.lesson) {
    return <ErrorComponent />;
  }

  const { challenge } = data;
  const lesson = challenge.lesson;

  if (!lesson || !workout) {
    return <ErrorComponent />;
  }

  const sectionsData = ((lesson.sections || []) as LessonSection[]).map((lessonSection) =>
    sectionToData({
      lessonSection: lessonSection as LessonSection,
      contentItemFocused,
      handleContentItemClick: handleSectionFocusChange,
      isMobile,
      theme,
      alternativeExercisesEnabled: false,
      expandNotesTextByDefault: true,
      hideExerciseChevrons: true,
      dataOnly: true, // Get groups data instead of rendered contentItems
    }),
  );

  // Challenge image (not lesson image) with placeholder fallback
  const challengeImageUrl = challenge.challengeImage?.signedUrl || '';

  const breakdown = <ChallengeBreakdown challenge={challenge} lesson={lesson} workout={workout} />;

  const sections = <ChallengeSections sections={sectionsData} />;

  const media = (
    <ChallengeMedia
      videoData={videoData}
      challengeImageUrl={challengeImageUrl}
      isSticky={!!contentItemFocused}
      autoPlay={!isMobile}
    />
  );

  const contentTitle = challenge.title;

  const canAccessPersonalisedHomescreen =
    userResourcesData?.user?.permissions?.canAccessPersonalisedHomescreen &&
    config.REACT_APP_PERSONALISED_HOMESCREEN_ENABLED;
  const resourceList = userResourcesData?.resourceList;

  return (
    <PageContainer>
      <HtmlTitle title={intl.formatMessage({ id: 'challenges.pageTitle' }, { title: challenge.title })} />
      <Navbar />
      <ContentWrapper>
        <Breadcrumb contentTitle={contentTitle} />
        <StickyContentWrapper>
          <DesktopStructure>
            <ChallengeTopLayer>
              <BreakdownWrapper>{breakdown}</BreakdownWrapper>
              {sections}
            </ChallengeTopLayer>
            {media}
          </DesktopStructure>
          <MobileStructure>
            <ChallengeTopLayer>
              <BreakdownWrapper>{breakdown}</BreakdownWrapper>
            </ChallengeTopLayer>
            {media}
            {sections}
          </MobileStructure>
        </StickyContentWrapper>
        <ResourcePageSection resourcePage={challenge.resourcePage} />
        {canAccessPersonalisedHomescreen && resourceList && (
          <MoreContentWrapper>
            <PlaybookTitle variant="h3">{intl.formatMessage({ id: 'resources.more' })}</PlaybookTitle>
            <ResourceListGrid resourceLists={resourceList.resourceLists || []} />
          </MoreContentWrapper>
        )}
      </ContentWrapper>
      <Footer />
    </PageContainer>
  );
};

export default Challenge;
